import { BooleanInput } from '@angular/cdk/coercion';
import { NgClass, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { UserService } from 'app/core/user/user.service';
import { User } from 'app/core/user/user.types';
import { Subject, takeUntil } from 'rxjs';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { UserProfileDialogComponent } from './user-profile-dialog.component';

@Component({
    selector: 'user',
    templateUrl: './user.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'user',
    imports: [MatButtonModule,
        MatMenuModule,
        NgIf,
        MatIconModule,
        NgClass,
        MatDividerModule]
})
export class UserComponent implements OnInit, OnDestroy
{
    static ngAcceptInputType_showAvatar: BooleanInput;

    @Input() showAvatar: boolean = true;
    user: User;

    private _unsubscribeAll: Subject<any> = new Subject<any>();
    private _dialog = inject(MatDialog);
    private _fuseConfirmationService = inject(FuseConfirmationService);

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _router: Router,
        private _userService: UserService,
        private _formBuilder: FormBuilder,
    )
    {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void
    {
        // Subscribe to user changes
        this._userService.user$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((user: User) =>
            {
                this.user = user;

                // Mark for check
                this._changeDetectorRef.markForCheck();
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Update the user status
     *
     * @param status
     */
    updateUserStatus(status: string): void
    {
        // Return if user is not available
        if ( !this.user )
        {
            return;
        }

        // Update the user
        this._userService.update({
            ...this.user,
            status,
        }).subscribe();
    }

    /**
     * Sign out
     */
    signOut(): void
    {
        this._router.navigate(['/sign-out']);
    }

    /**
     * Get role display name
     */
    getRoleName(role: string): string
    {
        const roleMap: Record<string, string> = {
            'admin': '🛡️ Administrador',
            'project_manager': '📁 Gestor de Proyectos',
            'writer': '✏️ Editor',
            'reader': '🔍 Lector'
        };
        return roleMap[role] || role;
    }

    /**
     * Get role badge classes
     */
    getRoleBadgeClasses(role: string): string
    {
        const classMap: Record<string, string> = {
            'admin': 'bg-red-100 text-red-800 dark:bg-red-600 dark:text-red-50',
            'project_manager': 'bg-blue-100 text-blue-800 dark:bg-blue-600 dark:text-blue-50',
            'writer': 'bg-green-100 text-green-800 dark:bg-green-600 dark:text-green-50',
            'reader': 'bg-gray-100 text-gray-800 dark:bg-gray-600 dark:text-gray-50'
        };
        return classMap[role] || 'bg-gray-100 text-gray-800 dark:bg-gray-600 dark:text-gray-50';
    }

    /**
     * Open profile edit dialog
     */
    openProfileDialog(): void
    {
        const dialogRef = this._dialog.open(UserProfileDialogComponent, {
            width: '500px',
            data: { user: this.user }
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                // Profile was updated, refresh user data
                this._changeDetectorRef.markForCheck();
            }
        });
    }
}
